const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export class ReportService {
    /**
     * Fetches the donor impact report for the given period.
     * @param {number} days - 30 or 90
     */
    static async getImpactReport(days = 30) {
        const response = await fetch(`${API_BASE_URL}/api/v3/report/impact?days=${days}`);
        if (!response.ok) {
            throw new Error(`Failed to load report: ${response.statusText}`);
        }
        return response.json();
    }

    /**
     * Starts the donor impact timeline computation in the background (a cold-cache
     * "since start" run can take a while) and returns { jobId, status }.
     */
    static async startImpactTimeline() {
        const response = await fetch(`${API_BASE_URL}/api/v3/report/timeline/start`, { method: 'POST' });
        if (!response.ok) {
            throw new Error(`Failed to start timeline report: ${response.statusText}`);
        }
        return response.json();
    }

    /**
     * Polls the status of a timeline job started via startImpactTimeline().
     * Returns { status: 'IN_PROGRESS' } | { status: 'DONE', result } | { status: 'ERROR', error }.
     */
    static async getImpactTimelineStatus(jobId) {
        const response = await fetch(`${API_BASE_URL}/api/v3/report/timeline/status/${jobId}`);
        if (!response.ok) {
            throw new Error(`Failed to check timeline report status: ${response.statusText}`);
        }
        return response.json();
    }

    /**
     * Starts a timeline job and polls until it completes, resolving with the timeline result.
     * @param {(elapsedSec: number) => void} [onTick] - called roughly once per poll interval.
     */
    static async getImpactTimeline(onTick, pollIntervalMs = 2000) {
        const { jobId } = await ReportService.startImpactTimeline();
        const startedAt = Date.now();

        // eslint-disable-next-line no-constant-condition
        while (true) {
            await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
            const status = await ReportService.getImpactTimelineStatus(jobId);
            if (onTick) onTick(Math.round((Date.now() - startedAt) / 1000));

            if (status.status === 'DONE') {
                return status.result;
            }
            if (status.status === 'ERROR') {
                throw new Error(status.error || 'Timeline computation failed');
            }
            // else IN_PROGRESS — keep polling
        }
    }
}
